import { useNavigate } from 'react-router-dom'
import { useReviews } from '../hooks/useReviews'
import { useCurrentUser } from '../components/AuthGuard/AuthGuard'
import type { Review } from '../gen/types'

function normalizeStatus(status: string): string {
  if (status === 'pending_human') return 'pending'
  return status
}

function formatAge(iso: string): string {
  try {
    const d = new Date(iso)
    const diffMs = Date.now() - d.getTime()
    const diffHours = Math.floor(diffMs / 3_600_000)
    if (diffHours < 1) return 'just now'
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

const PILL_BY_STATUS: Record<string, string> = {
  open: 'pill-open',
  pending: 'pill-pending',
  approved: 'pill-approved',
  rejected: 'pill-rejected',
  merged: 'pill-merged',
  abandoned: 'pill-abandoned',
}

function ReviewRow({ review }: { review: Review }) {
  const navigate = useNavigate()
  const norm = normalizeStatus(review.status)
  const attn = review.status === 'pending_human'
  const pillClass = PILL_BY_STATUS[norm] ?? 'pill-open'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/${review.tenantName}/${review.repoName}/reviews/${review.name}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(`/${review.tenantName}/${review.repoName}/reviews/${review.name}`)
      }}
      className={[
        'group bg-surface border border-border-default rounded-md cursor-pointer transition-colors',
        'border-l-[3px]',
        attn ? 'border-l-accent' : 'border-l-transparent',
        'px-4 py-3 flex flex-col gap-[7px] hover:bg-raised',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <span className="text-link font-semibold text-[12px]">{review.tenantName}/{review.repoName}</span>
        <span className="text-fg-dim text-[12px]">#{review.prNumber}</span>
        <span className={`pill ${pillClass}`}>{norm}</span>
        {attn && (
          <span className="attn-pill ml-auto">
            <span className="glow" />
            needs your decision
          </span>
        )}
      </div>
      <div className="flex gap-3 items-baseline justify-between">
        <div className="flex-1 min-w-0 truncate text-[13px] text-fg">{review.prTitle}</div>
        <div className="shrink-0 flex items-center gap-[7px] text-[11px] text-fg-dim">
          {review.prAuthorLogin && <span>@{review.prAuthorLogin}</span>}
          <span className="text-border-strong">·</span>
          <span>{formatAge(review.createdAt)}</span>
        </div>
      </div>
    </div>
  )
}

interface SectionProps {
  title: string
  count?: string
  reviews: Review[]
  emptyMsg: string
}

function DashboardSection({ title, count, reviews, emptyMsg }: SectionProps) {
  return (
    <section className="mt-7 first:mt-0">
      <h2 className="section-title">
        {title}
        {count && <span className="section-count">· {count}</span>}
      </h2>
      {reviews.length === 0 ? (
        <p className="text-fg-dim text-[12px] italic px-1 py-1">{emptyMsg}</p>
      ) : (
        <div className="flex flex-col gap-[6px]">
          {reviews.map((r) => (
            <ReviewRow key={r.id} review={r} />
          ))}
        </div>
      )}
    </section>
  )
}

export default function ReviewListPage() {
  const me = useCurrentUser()
  const { data: reviews = [], isLoading, isError, error } = useReviews()

  const ACTIVE = new Set(['open', 'pending_human'])
  const toReview = reviews.filter(
    (r) => r.prAuthorLogin !== me?.githubLogin && ACTIVE.has(r.status),
  )
  const myPRs = reviews.filter(
    (r) => r.prAuthorLogin === me?.githubLogin && ACTIVE.has(r.status),
  )
  const closed = reviews.filter((r) => r.status === 'merged' || r.status === 'approved')

  const attnCount = toReview.filter((r) => r.status === 'pending_human').length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-fg-dim text-[12px]">
        <span aria-hidden>⏳</span>
        Loading reviews…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sev-blocking text-[12px]">
        <span aria-hidden>⚠</span>
        {error instanceof Error ? error.message : 'Failed to load reviews'}
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-[900px] px-7 py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-fg text-[22px] font-bold tracking-[-0.005em] m-0">Reviews</h1>
          <div className="flex gap-2 items-center">
            <input
              className="input rounded-full px-3.5 py-[5px] text-[12px] w-[240px]"
              placeholder="filter — repo, author, status…"
            />
            <button className="btn btn-default text-[11px] normal-case">r:samik.sh</button>
            <button className="btn btn-default text-[11px] normal-case">state:open</button>
          </div>
        </div>

        <DashboardSection
          title="To Review"
          count={attnCount > 0 ? `${toReview.length} · ${attnCount} awaiting you` : `${toReview.length}`}
          reviews={toReview}
          emptyMsg="No PRs waiting for your review."
        />
        <DashboardSection
          title="Your PRs"
          count={`${myPRs.length}`}
          reviews={myPRs}
          emptyMsg="None of your PRs are under review."
        />
        <DashboardSection
          title="Closed"
          count={`${closed.length}`}
          reviews={closed}
          emptyMsg="No closed reviews yet."
        />
      </div>
    </div>
  )
}
